import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "@/lib/api";
import { getApiUrl } from "@/lib/api";
import { BlockEditor, ImageUploadButton } from "@/components/Tutorials/TutorialBlocks";
import { ArrowLeft, Plus, Pin, PinOff, Trash2, Pencil, Save, Loader2, FolderPlus, X, ImageIcon } from "lucide-react";
import { toast } from "sonner";

const API = `${getApiUrl()}/api`;

const EMPTY = { title: "", summary: "", categoryId: "", coverImage: "", blocks: [], published: true, pinned: false, pinnedOrder: 0 };

export default function TutorialsAdmin() {
  const navigate = useNavigate();
  const [tutorials, setTutorials] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null); // tutorial object being edited/created
  const [saving, setSaving] = useState(false);
  const [newCat, setNewCat] = useState("");

  const load = async () => {
    const [l, c] = await Promise.all([
      axios.get(`${API}/tutorials?sort=newest&include_unpublished=true`),
      axios.get(`${API}/tutorial-categories`),
    ]);
    setTutorials(l.data);
    setCategories(c.data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ ...EMPTY });
  const startEdit = async (id) => {
    const res = await axios.get(`${API}/tutorials/${id}`);
    setEditing(res.data);
  };

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const payload = {
        title: editing.title, summary: editing.summary, categoryId: editing.categoryId || null,
        coverImage: editing.coverImage || "",
        blocks: editing.blocks || [], published: editing.published, pinned: editing.pinned,
        pinnedOrder: editing.pinnedOrder || 0,
      };
      if (editing.id) await axios.put(`${API}/tutorials/${editing.id}`, payload);
      else await axios.post(`${API}/tutorials`, payload);
      toast.success("Tutorial saved");
      setEditing(null);
      load();
    } catch (e) { toast.error("Failed to save tutorial"); } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this tutorial permanently?")) return;
    await axios.delete(`${API}/tutorials/${id}`);
    toast.success("Deleted");
    load();
  };
  const togglePin = async (id) => { await axios.post(`${API}/tutorials/${id}/pin`); load(); };

  const addCategory = async () => {
    if (!newCat.trim()) return;
    await axios.post(`${API}/tutorial-categories`, { name: newCat.trim(), order: categories.length });
    setNewCat(""); load();
  };
  const delCategory = async (id) => {
    if (!window.confirm("Delete this category? Tutorials in it will become uncategorized.")) return;
    await axios.delete(`${API}/tutorial-categories/${id}`); load();
  };

  if (editing) {
    return (
      <div className="max-w-3xl mx-auto p-6 space-y-4" data-testid="tutorial-editor">
        <button onClick={() => setEditing(null)} className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white"><ArrowLeft className="w-4 h-4" /> Back</button>
        <h1 className="text-2xl font-bold text-white">{editing.id ? "Edit tutorial" : "New tutorial"}</h1>

        <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} placeholder="Tutorial title" className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white font-semibold focus:outline-none focus:border-cyan-400/50" data-testid="editor-title" />
        <textarea value={editing.summary} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} placeholder="Short summary (shown in the list)" rows={2} className="w-full bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-sm text-white/90 focus:outline-none" data-testid="editor-summary" />

        {/* Cover image */}
        <div className="rounded-lg border border-white/10 bg-black/20 p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-16 rounded-md bg-black/40 border border-white/10 overflow-hidden shrink-0 flex items-center justify-center">
                {editing.coverImage ? (
                  <img src={editing.coverImage} alt="cover" className="w-full h-full object-cover" data-testid="editor-cover-preview" />
                ) : (
                  <ImageIcon className="w-5 h-5 text-white/25" />
                )}
              </div>
              <div className="min-w-0">
                <p className="text-sm text-white/80 font-medium">Cover image</p>
                <p className="text-xs text-white/40">Shown as the tutorial's thumbnail in "What's New".</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <ImageUploadButton label={editing.coverImage ? "Replace" : "Upload cover"} onUploaded={(url) => setEditing({ ...editing, coverImage: url })} />
              {editing.coverImage && (
                <button type="button" onClick={() => setEditing({ ...editing, coverImage: "" })} className="text-xs inline-flex items-center gap-1 px-2 py-1 rounded bg-white/5 border border-white/10 text-white/60 hover:text-white" data-testid="editor-cover-remove">
                  <X className="w-3.5 h-3.5" /> Remove
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap text-sm">
          <select value={editing.categoryId || ""} onChange={(e) => setEditing({ ...editing, categoryId: e.target.value })} className="bg-black/30 border border-white/10 rounded-lg px-3 py-2 text-white/80" data-testid="editor-category">
            <option value="" className="bg-[#0A0A0A]">No category</option>
            {categories.map((c) => <option key={c.id} value={c.id} className="bg-[#0A0A0A]">{c.name}</option>)}
          </select>
          <label className="inline-flex items-center gap-1.5 text-white/70"><input type="checkbox" checked={editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} className="accent-cyan-500" /> Published</label>
          <label className="inline-flex items-center gap-1.5 text-white/70"><input type="checkbox" checked={editing.pinned} onChange={(e) => setEditing({ ...editing, pinned: e.target.checked })} className="accent-amber-500" /> Pinned</label>
          {editing.pinned && <input type="number" value={editing.pinnedOrder || 0} onChange={(e) => setEditing({ ...editing, pinnedOrder: parseInt(e.target.value) || 0 })} className="w-20 bg-black/30 border border-white/10 rounded px-2 py-1 text-white/80" title="Pin order" />}
        </div>

        <div className="pt-2">
          <h2 className="text-sm font-semibold text-white/70 mb-2">Content</h2>
          <BlockEditor blocks={editing.blocks || []} onChange={(blocks) => setEditing({ ...editing, blocks })} />
        </div>

        <div className="flex justify-end gap-2 pt-4">
          <button onClick={() => setEditing(null)} className="text-sm px-3 py-2 rounded border border-white/10 text-white/70">Cancel</button>
          <button onClick={save} disabled={saving} className="inline-flex items-center gap-1.5 text-sm px-4 py-2 rounded bg-cyan-500 hover:bg-cyan-600 text-white disabled:opacity-60" data-testid="editor-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save tutorial
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-8" data-testid="tutorials-admin">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={() => navigate("/tutorials")} className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-white"><ArrowLeft className="w-4 h-4" /> Back to Tutorials</button>
        <button onClick={startNew} className="inline-flex items-center gap-1.5 text-sm px-3 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-600 text-white" data-testid="new-tutorial-btn"><Plus className="w-4 h-4" /> New tutorial</button>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4 space-y-3">
        <h2 className="text-sm font-semibold text-white/80 flex items-center gap-2"><FolderPlus className="w-4 h-4" /> Categories</h2>
        <div className="flex flex-wrap gap-2">
          {categories.map((c) => (
            <span key={c.id} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded bg-white/5 border border-white/10 text-white/70">
              {c.name}
              <button onClick={() => delCategory(c.id)} className="text-white/40 hover:text-red-400" data-testid={`del-category-${c.id}`}><X className="w-3 h-3" /></button>
            </span>
          ))}
          {categories.length === 0 && <span className="text-xs text-white/40">No categories yet.</span>}
        </div>
        <div className="flex items-center gap-2">
          <input value={newCat} onChange={(e) => setNewCat(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addCategory()} placeholder="New category name" className="text-sm bg-black/30 border border-white/10 rounded px-2 py-1 text-white/80" data-testid="new-category-input" />
          <button onClick={addCategory} className="text-xs px-2.5 py-1.5 rounded bg-white/5 border border-white/10 text-white/70 hover:bg-white/10" data-testid="add-category-btn">Add</button>
        </div>
      </div>

      <div className="space-y-2">
        <h2 className="text-sm font-semibold text-white/80">All tutorials ({tutorials.length})</h2>
        {tutorials.map((t) => (
          <div key={t.id} className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3" data-testid={`admin-tutorial-${t.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-white font-medium truncate">{t.title}</span>
                {!t.published && <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-300 border border-yellow-400/20">Draft</span>}
                {t.pinned && <Pin className="w-3.5 h-3.5 text-amber-400" />}
              </div>
              {t.summary && <p className="text-xs text-white/45 truncate">{t.summary}</p>}
            </div>
            <button onClick={() => togglePin(t.id)} className="p-2 rounded hover:bg-white/10 text-white/60" title={t.pinned ? "Unpin" : "Pin"} data-testid={`pin-${t.id}`}>{t.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}</button>
            <button onClick={() => startEdit(t.id)} className="p-2 rounded hover:bg-white/10 text-cyan-300" title="Edit" data-testid={`edit-${t.id}`}><Pencil className="w-4 h-4" /></button>
            <button onClick={() => del(t.id)} className="p-2 rounded hover:bg-red-500/20 text-red-300" title="Delete" data-testid={`del-${t.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {tutorials.length === 0 && <p className="text-white/40 text-sm py-8 text-center">No tutorials yet. Click &ldquo;New tutorial&rdquo; to create one.</p>}
      </div>
    </div>
  );
}
